<script setup>
import MarketReadout from 'components/market/MarketReadout.vue';
import MarketSnapshot from 'components/market/MarketSnapshot.vue';
import { usePageMeta } from 'composables/page-meta';
import marketConfig, { findRelatedSpecialisationSlugs, TECH_MARKET_CLASSIFICATION_SLUG } from 'src/configs/market';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { createPageNavigation } from 'src/utils/error-page';
import { buildPageMeta } from 'src/utils/page-meta-util';
import { trackProductEvent } from 'src/utils/umami-util';
import { buildXprtJobsCategoryUrl } from 'src/utils/xprt-jobs-route-util';
import { useClassificationStore } from 'stores/classification-store';
import { useMarketStore } from 'stores/market-store';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

defineOptions({
    async preFetch({ currentRoute, redirect, ssrContext, store }) {
        const navigation = createPageNavigation({ currentRoute, redirect, ssrContext, store });
        if (currentRoute.params.classificationSlug === TECH_MARKET_CLASSIFICATION_SLUG) {
            return navigation.redirect(AppRoute.build(Route.MarketOverview), 301);
        }
        const marketStore = useMarketStore();
        try {
            const classification = await marketStore.loadClassification(currentRoute.params.classificationSlug);
            // A superseded request returns undefined; only null means the requested market does not exist.
            if (classification === null) {
                return navigation.raiseError(404);
            }
        } catch (err) {
            // Only a 404 means the page names nothing. Any other failure leaves a real page that cannot be served
            // right now, and 503 keeps its unavailable state out of search results without dropping the page.
            return navigation.raiseError(err?.response?.status === 404 ? 404 : 503);
        }
    }
});

const $route = useRoute();
const classificationStore = useClassificationStore();
const marketStore = useMarketStore();

const classificationResource = computed(() => {
    return marketStore.classification?.classification || classificationStore.marketClassifications.find(classification => {
        return classification.slug === $route.params.classificationSlug;
    }) || null;
});

const isKnownMarketClassification = computed(() => classificationResource.value !== null);

const classificationName = computed(() => {
    return classificationResource.value?.name || 'Market classification unavailable';
});

const classificationSlug = computed(() => classificationResource.value?.slug || null);

const canonicalUrl = computed(() => `https://xprt.dev/market/${classificationSlug.value}`);

const isParentClassification = computed(() => {
    const classification = classificationResource.value;
    return classification?.parent === null || classificationSlug.value === TECH_MARKET_CLASSIFICATION_SLUG;
});

const isMarketAvailable = computed(() => {
    return marketStore.classification !== null && marketStore.classificationError === null;
});

// Every figure on a page with nothing listed is empty or withheld, so the page says so once instead.
const isEmptyMarket = computed(() => !isParentClassification.value && marketStore.classification?.jobCount === 0);

const relatedSpecialisations = computed(() => {
    return findRelatedSpecialisationSlugs(classificationSlug.value)
        .map(slug => classificationStore.marketClassifications.find(classification => classification.slug === slug))
        .filter(Boolean);
});

const jobsUrl = computed(() => {
    return marketStore.classification?.classification?.slug
        ? buildXprtJobsCategoryUrl(marketStore.classification.classification.slug)
        : null;
});

const robotsDirective = computed(() => {
    if (!isKnownMarketClassification.value) {
        return 'noindex,nofollow';
    }
    return isEmptyMarket.value ? 'noindex, follow' : 'index, follow';
});

const marketDescription = computed(() => {
    return `Explore ${classificationName.value} job demand, advertised salaries, hiring employers and work arrangements `
        + 'across active Australian Xprt Jobs listings.';
});

usePageMeta(() => buildPageMeta({
    title: isKnownMarketClassification.value
        ? `${classificationName.value} Job Market in Australia`
        : 'Market classification unavailable',
    description: isKnownMarketClassification.value
        ? marketDescription.value
        : 'Australian ICT market classification details are currently unavailable.',
    canonicalUrl: isKnownMarketClassification.value ? canonicalUrl.value : null,
    robots: robotsDirective.value
}));

function trackJobsHandoff() {
    if (!classificationSlug.value) {
        return;
    }
    trackProductEvent('xprt_jobs_handoff', {
        component: 'market_classification',
        classificationSlug: classificationSlug.value,
        locationSlug: marketConfig.countrySlug
    });
}
</script>

<template>
    <main class="market-classification-page">
        <header class="market-hero">
            <div class="container-md q-px-md q-py-xl">
                <nav aria-label="Breadcrumb" class="text-body2 q-mb-lg">
                    <a class="text-weight-medium" href="/market">Australian ICT market</a>
                    <q-icon aria-hidden="true" class="q-mx-xs" name="sym_r_chevron_right"/>
                    <span aria-current="page" class="text-hint">{{ classificationName }}</span>
                </nav>
                <div class="hero-grid">
                    <div class="hero-copy">
                        <p class="font-mono text-caption text-uppercase text-hint q-mb-md">
                            {{ isParentClassification ? 'Australian ICT market' : 'Australian ICT specialisation' }} · Active listings
                        </p>
                        <h1 class="text-h3 text-sm-h2 text-md-h1 text-weight-bold q-mt-none q-mb-md">{{ classificationName }}</h1>
                        <p class="hero-deck text-h6 text-weight-regular text-hint q-mt-none q-mb-lg">
                            Active roles, advertised pay and the organisations hiring for this specialisation across Australia.
                        </p>
                        <q-btn
                            v-if="jobsUrl && !isEmptyMarket"
                            :href="jobsUrl"
                            color="primary"
                            data-test="classification-jobs-link"
                            icon-right="sym_r_north_east"
                            label="View jobs on Xprt Jobs"
                            no-caps
                            rel="noopener noreferrer"
                            target="_blank"
                            unelevated
                            @click="trackJobsHandoff"
                        />
                    </div>
                    <MarketReadout v-if="!isEmptyMarket" :snapshot="marketStore.classification"/>
                    <section v-else aria-labelledby="empty-market-heading" class="readout-frame q-pa-xl" role="status">
                        <p class="font-mono text-caption text-uppercase text-hint q-mt-none q-mb-sm">0 active listings</p>
                        <h2 id="empty-market-heading" class="text-h4 text-weight-bold q-mt-none q-mb-sm">
                            No active listings right now.
                        </h2>
                        <p class="text-body1 text-hint q-my-none">
                            No {{ classificationName }} roles are advertised on Xprt Jobs at the moment, so there is nothing
                            to measure. Similar roles are often listed under a related specialisation.
                        </p>
                    </section>
                </div>
            </div>
        </header>
        <div class="container-md q-px-md q-py-xl">
            <q-banner
                v-if="marketStore.classificationError && !marketStore.classification"
                class="unavailable-banner q-mb-xl"
                role="status"
            >
                <template #avatar>
                    <q-icon color="primary" name="sym_r_cloud_off"/>
                </template>
                <template v-if="isParentClassification">
                    <strong>The ICT parent market snapshot is currently unavailable.</strong>
                    <div class="text-body2 q-mt-xs">No child classification has been substituted for this parent market.</div>
                </template>
                <template v-else>
                    <strong>This specialisation snapshot is currently unavailable.</strong>
                    <div class="text-body2 q-mt-xs">No broader ICT result has been substituted for this specialisation.</div>
                </template>
            </q-banner>
            <MarketSnapshot
                v-if="!isEmptyMarket"
                :is-available="isMarketAvailable"
                :snapshot="marketStore.classification"
                hide-readout
            />
            <section v-if="relatedSpecialisations.length" aria-labelledby="related-heading" class="q-mt-xl">
                <h2 id="related-heading" class="text-h4 text-weight-bold q-mt-none q-mb-xs">Related specialisations</h2>
                <p class="text-body2 text-hint q-mt-none q-mb-md">
                    Advertisers choose the specialisation for each listing, so similar roles can sit under more than one.
                    Compare {{ classificationName }} with:
                </p>
                <ul class="related-list q-pl-none q-my-none">
                    <li v-for="related of relatedSpecialisations" :key="related.slug" class="q-py-sm">
                        <a :href="`/market/${related.slug}`" class="text-weight-medium" data-test="related-specialisation">
                            {{ related.name }}
                        </a>
                    </li>
                </ul>
            </section>
            <q-banner class="method-note q-pa-md q-mt-xl" role="note">
                <template #avatar>
                    <q-icon color="primary" name="sym_r_info"/>
                </template>
                <strong>{{ isParentClassification ? 'About this parent market' : 'No 30-day movement on this page' }}</strong>
                <div v-if="isParentClassification" class="text-body2 text-hint q-mt-xs">
                    This is the ICT parent classification. Its 30-day movement is shown on the Australian ICT overview,
                    where the parent series is presented in context.
                </div>
                <div v-else class="text-body2 text-hint q-mt-xs">
                    The 30-day trend is calculated for the broader ICT parent, not this specialisation, so it is left
                    out here rather than shown as if it were specific.
                </div>
                <template #action>
                    <q-btn color="primary" flat href="/methodology#note-3" label="Read note 3" no-caps/>
                </template>
            </q-banner>
        </div>
    </main>
</template>

<style lang="scss" scoped>
.market-classification-page {
    .hero-grid {
        display: grid;
        gap: 48px;
        grid-template-columns: 1fr;
    }

    .hero-copy {
        max-width: 760px;
    }

    .hero-deck {
        max-width: 640px;
    }

    .related-list {
        columns: 2;
        list-style: none;

        @media (max-width: $breakpoint-xs-max) {
            columns: 1;
        }
    }

    .unavailable-banner,
    .method-note {
        background: rgba($primary, 0.06);
        border: 1px solid rgba($primary, 0.16);
    }
}
</style>
